import { useEffect, useRef, useState } from 'react';
import { ShareGroup, ShareHost, ShareNote, ShareRow, WorkspaceSwitchedNotice, type ShareActionContext, type ShareMoveToProject, type UseShare } from '@flobi/asset-library/share';
import { useProjectsOptional, type ProjectCard } from '@flobi/asset-library/projects';
import '@flobi/asset-library/share.css';
import { useTheme } from '../theme';
import { moveFile } from '../home/api';
import { forgetSummaries } from '../projects/projectsApi';
import { artworkShareAdapter } from './artworkShare';
import { takeSwitchedWorkspace } from './switched';

/**
 * A file in no project is shared with nobody (decision 5): "Move to project ›" (the
 * adapter answers `inProject: false` only while the caller may move it). The list is the shared
 * Projects kit's (`project.list.forApp`: only projects this person can see, shown in Artwork, that
 * they can add to); moving asks first when it changes who can see the file (the kit's move
 * question, Decision 5), then `artwork.doc.move`, which decides who may.
 */
function ProjectPicker({ s, ctx }: { s: UseShare; ctx: ShareActionContext }) {
  const projectsKit = useProjectsOptional();
  const [projects, setProjects] = useState<ProjectCard[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => void (live.current = false);
  }, []);

  const load = async () => {
    setLoadError(false);
    if (!projectsKit) return setLoadError(true);
    try {
      const res = await projectsKit.adapter.listProjects({ app: 'artwork', tab: 'recent', sort: 'name', limit: 100 });
      if (live.current) setProjects(res.items.filter((one) => one.canAdd));
    } catch {
      if (live.current) setLoadError(true);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsKit?.adapter]);

  const move = async (project: ProjectCard) => {
    setMoving(project.id);
    setError(null);
    try {
      const owner = s.people.find((one) => one.kind === 'user' && one.role === 'owner');
      const others = s.people.filter((one) => one.kind === 'user' && one !== owner).map((one) => (one as { id: string }).id);
      const ok = projectsKit
        ? await projectsKit.confirmMove({
            projectId: project.id,
            fromProjectId: null,
            itemOwnerId: owner && owner.kind === 'user' ? owner.id : null,
            itemPeople: others,
            itemName: s.thing.name,
            targetName: project.name,
            noun: 'file',
          })
        : true;
      if (!ok) return;
      await moveFile(s.thing.id, project.id);
      forgetSummaries();
      s.reload();
      if (live.current) ctx.back();
    } catch (err) {
      if (live.current) setError((err as { userMessage?: string })?.userMessage || (err as Error)?.message || "Couldn't move the file. Try again.");
    } finally {
      if (live.current) setMoving(null);
    }
  };

  if (loadError) {
    return (
      <ShareNote role="alert" action="Try again" onAction={() => void load()}>
        Projects didn't load.
      </ShareNote>
    );
  }
  if (!projects) return <ShareNote glyph={null}>Loading projects…</ShareNote>;
  if (!projects.length) {
    return <ShareNote glyph={null}>There are no projects you can add files to yet. Make one in Projects first.</ShareNote>;
  }
  return (
    <>
      <ShareGroup footer={<ShareNote glyph={null}>Its people will be the project's people.</ShareNote>}>
        {projects.map((project) => (
          <ShareRow
            key={project.id}
            label={project.name}
            value={moving === project.id ? 'Moving…' : undefined}
            disabled={moving !== null}
            onClick={() => void move(project)}
          />
        ))}
      </ShareGroup>
      {error ? <ShareNote role="alert">{error}</ShareNote> : null}
    </>
  );
}

const moveToProject: ShareMoveToProject = { page: (s, ctx) => <ProjectPicker s={s} ctx={ctx} /> };

/**
 * The one Share / Invite mount for the app (SHARING-MODEL.md §3.1–3.2), in Artwork's own
 * light or dark. Everything opens it through share/artworkShare.ts.
 */
export function ArtworkShareHost() {
  const { theme } = useTheme();
  const mode = theme === 'light' ? 'light' : 'dark';
  // Set when the editor reloaded into another workspace to open a file that lives there (FileAccess).
  const [switched, setSwitched] = useState(() => takeSwitchedWorkspace());
  return (
    <>
      <ShareHost adapter={artworkShareAdapter} theme="artwork" mode={mode} moveToProject={moveToProject} />
      <WorkspaceSwitchedNotice workspace={switched} theme="artwork" mode={mode} onDone={() => setSwitched(null)} />
    </>
  );
}
